import React, { useEffect } from 'react';
import { Product, DocumentCategory } from '../types';
import { INITIAL_CATEGORIES } from '../lib/categoriesService';
import { getSafeProductImages, getSafeProductFirstImage } from '../lib/productImages';
import { getLocalSiteSettings } from '../lib/siteSettingsService';
import { buildProductSocialDescription, cleanDescriptionForSocial } from '../lib/descriptionUtils';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'product' | 'article';
  product?: Product | null;
  products?: Product[];
  activeTab?: string;
  selectedCategory?: DocumentCategory | 'All';
  selectedCountry?: string;
  searchQuery?: string;
}

const DEFAULT_TITLE = 'RH Editography | Premium Digital Template Marketplace';
const DEFAULT_DESCRIPTION = 'RH Editography is a premium marketplace for editable PSD templates, AI templates, Vector graphics, Passports, ID Cards, Driving Licenses, Certificates, Utility Bills, Bank Statements and professional digital resources.';
const DEFAULT_KEYWORDS = 'passport template, passport psd, passport vector, id card template, id card psd, driver license template, certificate template, bank statement template, visa template, identity document, security document, editable psd, illustrator template, figma template, graphic resources, digital download, 3d vector, premium template, document template, editable vector, Victor design, print design, RH Editography';
const DOMAIN = 'https://rheditography.net';
const DEFAULT_OG_IMAGE = 'https://rheditography.net/assets/logo.png';

export const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  product,
  products = [],
  activeTab = 'marketplace',
  selectedCategory = 'All',
  selectedCountry,
  searchQuery,
}) => {
  useEffect(() => {
    // 1. Determine Dynamic Title
    let pageTitle = DEFAULT_TITLE;
    if (product) {
      const pTitle = product.title || 'Template';
      const categoryTag = product.category ? `${product.category} Template` : 'Vector Design';
      pageTitle = `${pTitle} - ${categoryTag} PSD & AI Download | RH Editography`;
    } else if (selectedCategory && selectedCategory !== 'All') {
      pageTitle = `${selectedCategory} Templates & Vector PSD Designs | RH Editography`;
    } else if (selectedCountry) {
      pageTitle = `${selectedCountry} Passport & ID Card Vector Templates | RH Editography`;
    } else if (activeTab === 'search' || searchQuery) {
      pageTitle = `Search Results for "${searchQuery || 'Templates'}" | RH Editography`;
    } else if (activeTab === 'categories') {
      pageTitle = 'Browse All Document Categories & Design Templates | RH Editography';
    } else if (activeTab === 'countries') {
      pageTitle = 'Passport & ID Card Templates by Country | RH Editography';
    } else if (activeTab === 'contact') {
      pageTitle = 'Contact & 24/7 Support Desk | RH Editography';
    } else if (activeTab === 'user') {
      pageTitle = 'Customer Dashboard & Digital Downloads | RH Editography';
    } else if (title) {
      pageTitle = `${title} | RH Editography`;
    }

    document.title = pageTitle;

    // Helper to set/update meta tag
    const setMetaTag = (selector: string, attributeName: string, value: string) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        const match = selector.match(/meta\[([a-zA-Z0-9_:-]+)=['"]?([^'"]+)['"]?\]/);
        if (match) {
          element.setAttribute(match[1], match[2]);
        }
        document.head.appendChild(element);
      }
      element.setAttribute(attributeName, value);
    };

    // Helper to set/update link tag
    const setLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // 2. Determine Dynamic Description & Keywords
    const pageDesc = product
      ? buildProductSocialDescription(product)
      : selectedCountry
      ? `Explore high-resolution vector documents, passport, ID card, driving license, and bank statement PSD templates for ${selectedCountry}.`
      : cleanDescriptionForSocial(description) || DEFAULT_DESCRIPTION;

    const pageKeywords = keywords || DEFAULT_KEYWORDS;

    // 3. Determine Dynamic Canonical URL & Path
    let currentPath = '/';
    if (product) {
      const slug = product.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '');
      currentPath = `/product/${slug}`;
    } else if (selectedCategory && selectedCategory !== 'All') {
      const categorySlug = selectedCategory.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      currentPath = `/categories/${categorySlug}`;
    } else if (selectedCountry) {
      const countrySlug = selectedCountry.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      currentPath = `/countries/${countrySlug}`;
    } else if (activeTab === 'search' || searchQuery) {
      currentPath = `/search?q=${encodeURIComponent(searchQuery || '')}`;
    } else if (activeTab === 'categories') {
      currentPath = '/categories';
    } else if (activeTab === 'countries') {
      currentPath = '/countries';
    } else if (activeTab === 'contact') {
      currentPath = '/contact';
    } else if (activeTab === 'user') {
      currentPath = '/dashboard';
    }

    const fullCanonical = canonicalUrl || `${DOMAIN}${currentPath}`;
    const rawFirstImg = product ? getSafeProductFirstImage(product) : (ogImage || DEFAULT_OG_IMAGE);
    const prodPreviewUrl = (product as any)?.previewUrl || product?.previewImages?.[0];
    const pageOgImage = (rawFirstImg && rawFirstImg.startsWith('http')) ? rawFirstImg : (prodPreviewUrl && prodPreviewUrl.startsWith('http') ? prodPreviewUrl : `${DOMAIN}/assets/logo.png`);

    // Set Standard Meta Tags
    setMetaTag('meta[name="description"]', 'content', pageDesc);
    setMetaTag('meta[name="keywords"]', 'content', pageKeywords);
    setMetaTag('meta[name="robots"]', 'content', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    setMetaTag('meta[name="author"]', 'content', 'RH Editography');

    // Verification Readiness Meta Tags
    setMetaTag('meta[name="google-site-verification"]', 'content', 'RH-Editography-GSC-Verification-2026');
    setMetaTag('meta[name="msvalidate.01"]', 'content', 'RH-Editography-Bing-Verification-2026');

    // Open Graph
    setMetaTag('meta[property="og:type"]', 'content', product ? 'product' : ogType);
    setMetaTag('meta[property="og:title"]', 'content', pageTitle);
    setMetaTag('meta[property="og:description"]', 'content', pageDesc);
    setMetaTag('meta[property="og:url"]', 'content', fullCanonical);
    setMetaTag('meta[property="og:image"]', 'content', pageOgImage);
    setMetaTag('meta[property="og:image:secure_url"]', 'content', pageOgImage);
    setMetaTag('meta[property="og:image:width"]', 'content', '1200');
    setMetaTag('meta[property="og:image:height"]', 'content', '630');
    setMetaTag('meta[property="og:site_name"]', 'content', 'RH Editography');
    setMetaTag('meta[property="og:locale"]', 'content', 'en_US');

    // Twitter Cards
    setMetaTag('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMetaTag('meta[name="twitter:site"]', 'content', '@RhEditography');
    setMetaTag('meta[name="twitter:title"]', 'content', pageTitle);
    setMetaTag('meta[name="twitter:description"]', 'content', pageDesc);
    setMetaTag('meta[name="twitter:image"]', 'content', pageOgImage);

    // Canonical Link & Favicons
    setLinkTag('canonical', fullCanonical);
    const siteSettings = getLocalSiteSettings();
    if (siteSettings?.siteFaviconUrl?.trim()) {
      const customFavicon = siteSettings.siteFaviconUrl.trim();
      setLinkTag('icon', customFavicon);
      setLinkTag('shortcut icon', customFavicon);
      setLinkTag('apple-touch-icon', customFavicon);
    }
    
    // 4. Build JSON-LD Structured Data Graph
    const schemas: any[] = [
      // Organization Schema
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${DOMAIN}/#organization`,
        'name': 'RH Editography',
        'url': DOMAIN,
        'logo': `${DOMAIN}/assets/logo.png`,
        'description': DEFAULT_DESCRIPTION,
        'contactPoint': [
          {
            '@type': 'ContactPoint',
            'contactType': 'customer service',
            'email': 'support@rheditography.net',
            'availableLanguage': ['English', 'Bengali'],
          },
        ],
        'sameAs': [
          'https://t.me/RhEditography',
        ],
      },
      // WebSite Schema with SearchAction
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${DOMAIN}/#website`,
        'name': 'RH Editography',
        'url': DOMAIN,
        'description': 'Premium Marketplace for Professional Vector Editable Templates & Digital Graphic Assets',
        'publisher': {
          '@id': `${DOMAIN}/#organization`,
        },
        'potentialAction': {
          '@type': 'SearchAction',
          'target': {
            '@type': 'EntryPoint',
            'urlTemplate': `${DOMAIN}/?search={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },

      // WebPage Schema
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${fullCanonical}#webpage`,
        'url': fullCanonical,
        'name': pageTitle,
        'description': pageDesc,
        'isPartOf': {
          '@id': `${DOMAIN}/#website`,
        },
        'about': {
          '@id': `${DOMAIN}/#organization`,
        },
      },

      // BreadcrumbList Schema
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': 'Home',
            'item': DOMAIN,
          },
          ...(activeTab !== 'marketplace'
            ? [
                {
                  '@type': 'ListItem',
                  'position': 2,
                  'name': activeTab.charAt(0).toUpperCase() + activeTab.slice(1),
                  'item': `${DOMAIN}/${activeTab}`,
                },
              ]
            : []),
          ...(selectedCategory && selectedCategory !== 'All'
            ? [
                {
                  '@type': 'ListItem',
                  'position': activeTab !== 'marketplace' ? 3 : 2,
                  'name': selectedCategory,
                  'item': `${DOMAIN}/categories/${selectedCategory.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
                },
              ]
            : []),
          ...(product
            ? [
                {
                  '@type': 'ListItem',
                  'position': 3,
                  'name': product.title,
                  'item': fullCanonical,
                },
              ]
            : []),
        ],
      },
    ];

    // If viewing a Product, inject Product & Offer Schema
    if (product) {
      const productImages = getSafeProductImages(product);
      const productSchema: any = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        'name': product.title,
        ...(productImages.length > 0 ? { 'image': productImages } : {}),
        'description': cleanDescriptionForSocial(product.description, product.title),
        'sku': product.id,
        'category': product.category,
        'brand': {
          '@type': 'Brand',
          'name': 'RH Editography',
        },
        'offers': {
          '@type': 'Offer',
          'url': fullCanonical,
          'priceCurrency': 'USD',
          'price': product.price !== undefined ? product.price : 0,
          'priceValidUntil': '2027-12-31',
          'itemCondition': 'https://schema.org/NewCondition',
          'availability': 'https://schema.org/InStock',
          'seller': {
            '@type': 'Organization',
            'name': 'RH Editography',
          },
        },
      };

      if (product.rating && product.reviewCount) {
        productSchema.aggregateRating = {
          '@type': 'AggregateRating',
          'ratingValue': product.rating,
          'reviewCount': product.reviewCount,
          'bestRating': 5,
          'worstRating': 1,
        };
      }

      schemas.push(productSchema);
    }

    // CollectionPage & ItemList Schema when viewing categories or category filter
    if (activeTab === 'categories' || (selectedCategory && selectedCategory !== 'All')) {
      const itemsToDisplay = selectedCategory === 'All'
        ? products
        : products.filter(p => p.category === selectedCategory);

      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        'name': selectedCategory !== 'All' ? `${selectedCategory} Vector & PSD Templates` : 'All Document Template Categories',
        'url': fullCanonical,
        'description': `Browse high-quality editable ${selectedCategory} templates in PSD, AI, CDR and DOCX formats at RH Editography.`,
        'mainEntity': {
          '@type': 'ItemList',
          'itemListElement': itemsToDisplay.slice(0, 15).map((item, idx) => ({
            '@type': 'ListItem',
            'position': idx + 1,
            'name': item.title,
            'url': `${DOMAIN}/products/${item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '')}`,
          })),
        },
      });
    }

    // FAQ Schema
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'What file formats are provided in RH Editography vector templates?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'All document templates come with high-resolution Adobe Photoshop (PSD), Illustrator (AI/EPS), CorelDRAW (CDR), and editable Microsoft Word (DOCX) files with smart photo layers.',
          },
        },
        {
          '@type': 'Question',
          'name': 'How fast will I receive my digital download after purchase?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Digital product downloads are instant. Right after order confirmation, high-speed download links appear on screen and in your RH Editography account dashboard.',
          },
        },
        {
          '@type': 'Question',
          'name': 'Are these templates editable with custom photos and details?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Yes, every template features 100% organized smart object layers, full vector paths, font links, and editable text fields.',
          },
        },
      ],
    });

    // Update JSON-LD script tag in head
    let scriptElement = document.getElementById('jsonld-schema-data') as HTMLScriptElement;
    if (!scriptElement) {
      scriptElement = document.createElement('script');
      scriptElement.id = 'jsonld-schema-data';
      scriptElement.type = 'application/ld+json';
      document.head.appendChild(scriptElement);
    }
    scriptElement.textContent = JSON.stringify(schemas);

  }, [title, description, keywords, canonicalUrl, ogImage, ogType, product, activeTab, selectedCategory]);

  return null;
};

